import React from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { BED_STATUS } from '../contexts/FirestoreBedManagementContext';
import { Bed, User, Clock, AlertTriangle, Wrench, Lock, Sparkles } from 'lucide-react';

const BedCard = ({ bed, zoneColor }) => {
  const isDraggable = bed.status === BED_STATUS.OCCUPIED;
  
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    transform,
    isDragging,
  } = useDraggable({
    id: bed.id,
    disabled: !isDraggable,
  });
  
  const { isOver, setNodeRef: setDropRef } = useDroppable({
    id: bed.id,
    disabled: bed.status !== BED_STATUS.AVAILABLE,
  });
  
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };
  
  // Determine bed color based on status
  const getBedColor = () => {
    switch (bed.status) {
      case BED_STATUS.AVAILABLE:
        return { bg: 'bg-green-100', border: 'border-green-300', icon: 'text-green-600' };
      case BED_STATUS.OCCUPIED:
        return { bg: 'bg-blue-100', border: 'border-blue-300', icon: 'text-blue-600' };
      case BED_STATUS.CLEANING:
        return { bg: 'bg-yellow-100', border: 'border-yellow-300', icon: 'text-yellow-600' };
      case BED_STATUS.MAINTENANCE:
        return { bg: 'bg-gray-100', border: 'border-gray-300', icon: 'text-gray-600' };
      case BED_STATUS.RESERVED:
        return { bg: 'bg-purple-100', border: 'border-purple-300', icon: 'text-purple-600' };
      default:
        return { bg: 'bg-gray-100', border: 'border-gray-300', icon: 'text-gray-600' };
    }
  };
  
  const colors = getBedColor();
  
  // Get status icon
  const getStatusIcon = () => {
    switch (bed.status) {
      case BED_STATUS.AVAILABLE:
        return <Bed className="h-4 w-4" />;
      case BED_STATUS.OCCUPIED:
        return <User className="h-4 w-4" />;
      case BED_STATUS.CLEANING:
        return <Sparkles className="h-4 w-4" />;
      case BED_STATUS.MAINTENANCE:
        return <Wrench className="h-4 w-4" />;
      case BED_STATUS.RESERVED:
        return <Lock className="h-4 w-4" />;
      default:
        return <Bed className="h-4 w-4" />;
    }
  };
  
  // Calculate time in bed
  const getTimeInBed = () => {
    if (!bed.assignedAt) return null;
    const hours = Math.floor((Date.now() - new Date(bed.assignedAt).getTime()) / (1000 * 60 * 60));
    return hours;
  };
  
  const timeInBed = getTimeInBed();
  
  return (
    <div
      ref={(node) => {
        setDragRef(node);
        setDropRef(node);
      }}
      style={style}
      {...(isDraggable ? { ...listeners, ...attributes } : {})}
      className={`
        relative w-16 h-16 rounded-lg border-2 transition-all cursor-pointer
        ${colors.bg} ${colors.border}
        ${isOver ? 'ring-2 ring-blue-400 shadow-lg scale-105' : ''}
        ${isDragging ? 'cursor-grabbing z-50' : ''}
        ${bed.status === BED_STATUS.AVAILABLE ? 'hover:shadow-md' : ''}
        group
      `}
      title={`Bed ${bed.number} - ${bed.status}`}
    >
      {/* Bed Number */}
      <div className="absolute top-1 left-1 text-xs font-bold text-gray-700">
        {bed.number}
      </div>
      
      {/* Status Icon */}
      <div className={`absolute bottom-1 right-1 ${colors.icon}`}>
        {getStatusIcon()}
      </div>
      
      {/* ESI Level for occupied beds */}
      {bed.patient && (
        <div 
          className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
          style={{ 
            backgroundColor: bed.patient.esiLevel === 1 ? '#dc2626' :
                           bed.patient.esiLevel === 2 ? '#ea580c' :
                           bed.patient.esiLevel === 3 ? '#ca8a04' :
                           bed.patient.esiLevel === 4 ? '#16a34a' : '#2563eb'
          }}
        >
          {bed.patient.esiLevel}
        </div>
      )}
      
      {/* Time indicator for long stays */}
      {timeInBed && timeInBed > 4 && (
        <div className="absolute -top-2 -left-2 bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
          <Clock className="h-3 w-3" />
        </div>
      )}
      
      {/* Features indicators */}
      {bed.features?.includes('isolation') && (
        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
          <AlertTriangle className="h-3 w-3 text-orange-500" />
        </div>
      )}
      
      {/* Hover tooltip */}
      <div className="opacity-0 group-hover:opacity-100 absolute z-10 bottom-full mb-2 left-1/2 transform -translate-x-1/2 pointer-events-none transition-opacity">
        <div className="bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap">
          <div className="font-semibold">Bed {bed.number}</div>
          <div className="text-gray-300 mt-1">Status: {bed.status}</div>
          {bed.patient && (
            <>
              <div className="text-gray-300">ESI-{bed.patient.esiLevel}</div>
              {timeInBed && <div className="text-gray-300">{timeInBed}h in bed</div>}
            </>
          )}
          {bed.features && bed.features.length > 0 && (
            <div className="text-gray-300 mt-1">{bed.features.join(', ')}</div>
          )}
        </div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
      </div>
    </div>
  );
};

export default BedCard; 